// Lazy-loaded Cloudflare Turnstile helper. Renders a single invisible widget,
// exposes getToken() as a Promise. When REACT_APP_TURNSTILE_SITE_KEY is unset
// (typical local dev), getToken() resolves to '' so the backend's matching
// dev-bypass on TURNSTILE_SECRET_KEY just lets the request through.

const SITE_KEY = process.env.REACT_APP_TURNSTILE_SITE_KEY || '';
const SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let scriptLoadPromise = null;
let widgetId = null;
let containerEl = null;

const ensureScript = () => {
  if (scriptLoadPromise) return scriptLoadPromise;

  scriptLoadPromise = new Promise((resolve, reject) => {
    if (typeof window === 'undefined') {
      reject(new Error('Turnstile requires a browser environment'));
      return;
    }
    if (window.turnstile) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = SCRIPT_URL;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Failed to load Turnstile script'));
    document.head.appendChild(script);
  });

  return scriptLoadPromise;
};

const ensureContainer = () => {
  if (containerEl) return containerEl;
  containerEl = document.createElement('div');
  containerEl.id = 'cf-turnstile-host';
  containerEl.style.position = 'fixed';
  containerEl.style.left = '-9999px';
  containerEl.style.top = '-9999px';
  containerEl.style.width = '0';
  containerEl.style.height = '0';
  containerEl.setAttribute('aria-hidden', 'true');
  document.body.appendChild(containerEl);
  return containerEl;
};

const renderWidget = () => new Promise((resolve, reject) => {
  if (widgetId !== null) {
    resolve(widgetId);
    return;
  }
  try {
    widgetId = window.turnstile.render(ensureContainer(), {
      sitekey: SITE_KEY,
      size: 'invisible',
      callback: () => {} // tokens fetched on demand via execute()
    });
    resolve(widgetId);
  } catch (error) {
    reject(error);
  }
});

export const isTurnstileEnabled = () => Boolean(SITE_KEY);

export const getTurnstileToken = async () => {
  if (!SITE_KEY) {
    return '';
  }
  try {
    await ensureScript();
    if (!window.turnstile) {
      return '';
    }
    await renderWidget();
    // execute() returns a token after solving the challenge. With invisible
    // widget + low-difficulty traffic this typically resolves in <300ms.
    const token = await new Promise((resolve, reject) => {
      const timeout = setTimeout(() => reject(new Error('Turnstile execute timed out')), 8000);
      try {
        window.turnstile.execute(widgetId, {
          callback: (value) => {
            clearTimeout(timeout);
            resolve(value || '');
          },
          'error-callback': () => {
            clearTimeout(timeout);
            resolve('');
          }
        });
      } catch (error) {
        clearTimeout(timeout);
        reject(error);
      }
    });
    // Reset so the next call gets a fresh token.
    try { window.turnstile.reset(widgetId); } catch (_) { /* ignore */ }
    return token || '';
  } catch (error) {
    console.warn('[Turnstile] token fetch failed:', error?.message || error);
    return '';
  }
};
